import { useEffect, useRef } from 'react';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useExecution, useCancelExecution, useRetryExecution } from '../../../api/media-sim/executions';
import { useProject } from '../../../api/media-sim/projects';
import { useRunPublications } from '../../../api/media-sim/runs';
import { StatusBadge } from '../shared/StatusBadge';
import { ProgressBar } from '../shared/ProgressBar';
import { SimulationResults } from './SimulationResults';
import { SimulationProgress } from './SimulationProgress';
import type { ReflectionProgress as ReflectionProgressType, ReflectionProgressItem, ExecutionWithSimulations } from '../../../types/media-sim';

interface ExecutionStatusProps {
  projectId: string;
  runId: string;
  executionId: string;
  onCompleted?: () => void;
  /** Render the full article results inline when completed. Defaults to true;
   *  the Runs page sets this false so articles live only on the Results page. */
  showResults?: boolean;
  /** Hide the internal "Execution Status" heading — used when a parent renders
   *  the title above a Sample/Full tab set. */
  hideTitle?: boolean;
  /** Called after a successful retry so the parent can refresh its own state
   *  (e.g. the executions list backing the Sample/Full tabs). */
  onRetried?: () => void;
}

const POLL_INTERVAL_MS = 4000;

function PublicationReflectionRow({
  publisher,
  status,
  progressPercent,
  errorMessage,
}: {
  publisher: string;
  status: string;
  progressPercent: number;
  errorMessage?: string | null;
}) {
  const { theme } = useTheme();
  return (
    <Card variant="outlined" padding="sm">
      <Card.Body>
        <Stack gap="xs">
          <Flex align="center" gap="sm">
            <span
              style={{
                fontWeight: 600,
                fontSize: '0.85rem',
                color: theme.colors.text.primary,
                minWidth: 140,
              }}
            >
              {publisher}
            </span>
            <StatusBadge status={status} size="sm" />
            <div style={{ flex: 1 }}>
              <ProgressBar value={progressPercent} size="sm" showLabel />
            </div>
          </Flex>
          {status === 'failed' && errorMessage && (
            <p
              style={{
                margin: 0,
                fontSize: '0.75rem',
                color: theme.colors.semantic.error.default,
                wordBreak: 'break-word',
              }}
            >
              {errorMessage}
            </p>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
}

function ReflectionTypeSection({
  title,
  items,
  terms,
  pendingHint,
  publications,
}: {
  title: string;
  items: ReflectionProgressItem[];
  terms?: string[];
  /** Message shown when this phase hasn't produced any rows yet, so users can
   *  see what's coming next rather than the section being hidden entirely. */
  pendingHint?: string;
  /** Run publications, used to render per-publication "pending" rows before the
   *  phase has produced its own progress rows. */
  publications?: { id: string; publisher: string }[];
}) {
  const { theme } = useTheme();

  // No rows yet → show a "Pending" section that still lists each publication as
  // a pending inner card (matching the populated / Article Generation layout),
  // so users see the same structure throughout the lifecycle.
  if (items.length === 0) {
    return (
      <Card variant="elevated" padding="sm">
        <Card.Header title={title} subtitle="Pending" />
        <Card.Body>
          <Stack gap="sm">
            {pendingHint && (
              <p style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, margin: 0 }}>
                {pendingHint}
              </p>
            )}
            {(publications ?? []).map((pub) => (
              <PublicationReflectionRow
                key={pub.id}
                publisher={pub.publisher}
                status="pending"
                progressPercent={0}
              />
            ))}
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  const allCompleted = items.every((i) => i.status === 'completed');
  const anyRunning = items.some((i) => i.status === 'running');

  return (
    <Card variant="elevated" padding="sm">
      <Card.Header
        title={title}
        subtitle={
          allCompleted ? 'Completed' : anyRunning ? 'In progress...' : 'Pending'
        }
      />
      <Card.Body>
        <Stack gap="sm">
          {terms && terms.length > 0 && (
            <p style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, margin: 0 }}>
              Generated from terms: {terms.join(', ')}
            </p>
          )}
          {items.map((item) => (
            <PublicationReflectionRow
              key={item.publication_id}
              publisher={item.publisher}
              status={item.status}
              progressPercent={item.progress_percent}
              errorMessage={item.error_message}
            />
          ))}
        </Stack>
      </Card.Body>
    </Card>
  );
}

export function ExecutionStatus({ projectId, runId, executionId, onCompleted, showResults = true, hideTitle = false, onRetried }: ExecutionStatusProps) {
  const { theme } = useTheme();
  const { execution, loading, error, refetch } = useExecution(projectId, runId, executionId);
  const { data: project } = useProject(projectId);
  const { data: runPublications } = useRunPublications(projectId, runId);
  const { cancel, loading: cancelling } = useCancelExecution();
  const { retry, loading: retrying } = useRetryExecution();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleCancel = async () => {
    await cancel(projectId, runId, executionId);
    refetch();
  };

  const handleRetry = async () => {
    await retry(projectId, runId, executionId);
    refetch();
    onRetried?.();
  };

  // Polling: refetch every 4s while pending or running
  useEffect(() => {
    if (execution?.status === 'pending' || execution?.status === 'running') {
      intervalRef.current = setInterval(() => {
        refetch();
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [execution?.status, refetch]);

  // Notify parent when execution completes. Must run before any early return so
  // the hook order stays stable across renders (Rules of Hooks).
  useEffect(() => {
    if (execution?.status === 'completed' || execution?.status === 'failed') {
      onCompleted?.();
    }
  }, [execution?.status, onCompleted]);

  if (loading && !execution) {
    return (
      <Card variant="outlined">
        <Card.Header title="Execution Status" />
        <Card.Body>
          <Stack gap="md">
            <div style={{ height: 12, borderRadius: 4, backgroundColor: theme.colors.neutral[200], width: '40%' }} />
            <div style={{ height: 10, borderRadius: 5, backgroundColor: theme.colors.neutral[200], width: '100%' }} />
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ height: 14, borderRadius: 4, backgroundColor: theme.colors.neutral[200], width: `${60 + i * 10}%` }} />
            ))}
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  if (error || !execution) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.semantic.error.default }}>
            {error?.message || 'Execution not found'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  const phaseLabel =
    execution.current_phase === 'static'
      ? 'Generating Static Reflections'
      : execution.current_phase === 'dynamic_topic'
        ? 'Generating Topic Reflections'
        : execution.current_phase === 'dynamic_subject'
          ? 'Generating Subject Reflections'
          : execution.current_phase === 'article_generation'
            ? 'Generating Articles'
            : execution.current_phase === 'simulation'
              ? 'Generating Articles'
              : execution.current_phase === 'completed'
                ? 'Completed'
                : execution.current_phase ?? '';

  const rp: ReflectionProgressType | null = execution.reflection_progress;
  const executionWithSim = execution as ExecutionWithSimulations;

  return (
    <Stack gap="md">
      {!hideTitle && (
        <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: theme.colors.text.primary, margin: `${spacing.lg} 0 0` }}>
          Execution Status
        </h2>
      )}

      {/* Overall status — emphasized (accent border + tinted background) so it
          reads as the top-level tracker, distinct from the per-phase sub-cards. */}
      <div
        style={{
          border: `2px solid ${theme.colors.primary[500]}`,
          borderRadius: '12px',
          background: `${theme.colors.primary[500]}0D`,
          padding: spacing.lg,
        }}
      >
        <Stack gap="md">
          <Flex justify="between" align="center" wrap="wrap" gap="sm">
            <div>
              <p style={{
                margin: 0,
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: theme.colors.primary[600],
              }}>
                Overall Progress
              </p>
              <span style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.colors.text.primary }}>
                {execution.execution_type === 'sample' ? 'Sample' : 'Full'} Execution
              </span>
            </div>
            <Flex align="center" gap="sm">
              <StatusBadge status={execution.status} size="md" />
              {phaseLabel && (
                <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                  {phaseLabel}
                </span>
              )}
            </Flex>
          </Flex>

          <ProgressBar
            value={execution.progress_percent}
            size="md"
            color={execution.status === 'failed' ? 'error' : 'primary'}
            showLabel
          />

            {(execution.status === 'running' || execution.status === 'pending') && (
              <Flex justify="end">
                <Button
                  variant="outline"
                  color="error"
                  size="sm"
                  onClick={handleCancel}
                  loading={cancelling}
                  disabled={cancelling}
                >
                  Cancel Execution
                </Button>
              </Flex>
            )}

            {execution.status === 'failed' && (
              <Stack gap="sm">
                {execution.error_message && (
                  <Card variant="outlined" padding="sm">
                    <Card.Body>
                      <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
                        {execution.error_message}
                      </p>
                    </Card.Body>
                  </Card>
                )}
                <Flex justify="between" align="center" gap="sm">
                  <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                    Retrying re-runs the failed steps (reflection notes and/or articles) and
                    keeps everything that already completed.
                  </span>
                  <Button
                    variant="solid"
                    color="primary"
                    size="sm"
                    onClick={handleRetry}
                    loading={retrying}
                    disabled={retrying}
                  >
                    Retry Failed Steps
                  </Button>
                </Flex>
              </Stack>
            )}
          </Stack>
      </div>

      {rp && (
        <Stack gap="sm">
          {/* All phases render from the start (as "Pending" until they produce
              rows) so users can see the sequence: static → topic → subject.
              Subject only applies when the project has subject terms. */}
          <ReflectionTypeSection
            title="Static Reflections"
            items={rp.static}
            publications={runPublications ?? undefined}
            pendingHint="Analyzing each publication's editorial style — this runs first."
          />
          <ReflectionTypeSection
            title="Topic Reflections"
            items={rp.dynamic_topic}
            publications={runPublications ?? undefined}
            pendingHint="Runs after static reflections complete."
          />
          {(rp.dynamic_subject.length > 0 || (project?.subject_terms?.length ?? 0) > 0) && (
            <ReflectionTypeSection
              title="Subject Reflections"
              items={rp.dynamic_subject}
              publications={runPublications ?? undefined}
              pendingHint="Runs after topic reflections complete."
            />
          )}
        </Stack>
      )}

      {executionWithSim.simulation_progress && (
        <SimulationProgress
          progress={executionWithSim.simulation_progress}
          totalCostCents={execution.total_cost_cents}
        />
      )}

      {rp && (
        <div style={{ padding: `0 ${spacing.xs}` }}>
          <Stack gap="xs">
            <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: theme.colors.text.secondary }}>
              About reflection notes
            </p>
            <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.6, color: theme.colors.text.secondary }}>
              <strong>Static</strong> reflections capture a publication's general
              editorial style and are reused across runs — they regenerate only when a
              publication has no current static notes. <strong>Topic</strong> and{' '}
              <strong>Subject</strong> reflections are specific to this run's terms and are
              generated per execution; a full simulation reuses the sample's topic/subject
              notes when the sample completed within the last 12 hours, and regenerates them
              otherwise.
            </p>
            <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.6, color: theme.colors.text.secondary }}>
              The generated notes — and the source articles used — can be viewed on the{' '}
              <strong>Results</strong> page for this execution. To see when a publication's
              static notes were last generated, visit <strong>Explore Publications</strong>.
            </p>
          </Stack>
        </div>
      )}

      <div style={{ padding: `0 ${spacing.xs}` }}>
        <Stack gap="xs">
          <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: theme.colors.text.secondary }}>
            About cost
          </p>
          <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.6, color: theme.colors.text.secondary }}>
            The cost shown is an <strong>estimate</strong>, not exact billing. It is the total for
            this execution — combining the tokens used for <strong>reflection generation</strong>{' '}
            (static, topic, and subject notes) and <strong>article simulation</strong>, priced at
            approximate per-token rates and summed across every attempt (so retrying failed steps
            adds to, rather than replaces, the running total). Actual charges depend on the specific
            models used at each stage and may differ.
          </p>
        </Stack>
      </div>

      {execution.warnings && execution.warnings.length > 0 && (
        <Card variant="outlined" padding="sm">
          <Card.Header title="Warnings" />
          <Card.Body>
            <ul style={{ margin: 0, paddingLeft: spacing.md }}>
              {execution.warnings.map((w, i) => (
                <li key={i} style={{ color: theme.colors.semantic.warning.default, fontSize: '0.85rem' }}>
                  {w}
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {showResults && execution.status === 'completed' && (
        <SimulationResults
          projectId={projectId}
          runId={runId}
          executionId={executionId}
        />
      )}
    </Stack>
  );
}
